// Client API minimale per la chat utente.
// Inviamo `credentials: 'include'` per supportare future cookie-based features.

import { z } from 'zod';
import {
  PublicFlowSchema,
  ApiErrorSchema,
  type PublicFlow,
  type IdentityField,
} from '@audiq/shared';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, message: string, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errParsed = ApiErrorSchema.safeParse(json);
    throw new ApiError(
      res.status,
      errParsed.success ? errParsed.data.error : 'Errore sconosciuto',
      errParsed.success ? (errParsed.data.code ?? 'UNKNOWN') : 'UNKNOWN',
    );
  }
  return json as T;
}

const FlowActiveResponseSchema = z.object({
  ok: z.literal(true),
  flow: PublicFlowSchema,
});
export async function getActiveFlow(): Promise<PublicFlow> {
  const res = await request<unknown>('/api/flow/active');
  return FlowActiveResponseSchema.parse(res).flow;
}

// Sprint 7: branding pubblico include kiosk_mode_enabled + welcome message.
const BrandingResponseSchema = z.object({
  ok: z.literal(true),
  display_name: z.string(),
  logo_url: z.string().nullable(),
  organization_name: z.string().nullable(),
  primary_color: z.string().nullable(),
  accent_color: z.string().nullable(),
  font_override: z.string().nullable(),
  privacy_policy_url: z.string().nullable(),
  privacy_dpo_email: z.string().nullable(),
  kiosk_mode_enabled: z.boolean().default(false),
  kiosk_idle_reset_seconds: z.number().int().default(30),
  kiosk_welcome_message: z.string().nullable().default(null),
  // Sprint 8 — voice mode
  voice_enabled: z.boolean().default(false),
  voice_consent_required: z.boolean().default(false),
});
export type Branding = z.infer<typeof BrandingResponseSchema>;
export async function getBranding(): Promise<Branding> {
  const res = await request<unknown>('/api/branding');
  return BrandingResponseSchema.parse(res);
}

// Sprint 7: verifica PIN kiosk (per uscire dalla modalità kiosk).
export async function verifyKioskPin(pin: string): Promise<boolean> {
  const res = await fetch('/api/kiosk/verify-pin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ pin }),
  });
  const json = (await res.json().catch(() => ({}))) as { ok?: boolean };
  return Boolean(json.ok);
}

export interface SessionStartRequest {
  identity?: Partial<Record<IdentityField, string>>;
  consents: {
    data_processing: true;
    extra_eu_transfer: true;
    audio_processing: boolean;
    informativa_acknowledged: true;
    consent_text_version: string;
    consent_text_lang: string;
  };
}

const SessionStartResponseSchema = z.object({
  ok: z.literal(true),
  session_id: z.string().uuid(),
  pseudonym: z.string(),
  pickup_code: z.string(),
});
export async function startSession(
  body: SessionStartRequest,
): Promise<{ session_id: string; pseudonym: string; pickup_code: string }> {
  const res = await request<unknown>('/api/session/start', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return SessionStartResponseSchema.parse(res);
}

// Sprint additivo 2026-05-05: invio identità a fine chat (timing: 'at_end').
const SessionIdentityAtEndResponseSchema = z.object({ ok: z.literal(true) });
export async function submitIdentityAtEnd(
  sessionId: string,
  identity: Partial<Record<IdentityField, string>>,
): Promise<void> {
  const res = await request<unknown>(`/api/session/${encodeURIComponent(sessionId)}/identity`, {
    method: 'POST',
    body: JSON.stringify({ identity }),
  });
  SessionIdentityAtEndResponseSchema.parse(res);
}

// ====================================================================
// Sprint 3: turn (SSE streaming) + resume
// ====================================================================

export type TurnStreamEvent =
  | { type: 'text_delta'; text: string }
  | {
      type: 'final';
      status: 'in_progress' | 'completed' | 'aborted_emergency';
      next_action:
        | 'advance'
        | 'retry_same'
        | 'request_clarification'
        | 'emergency_exit'
        | 'complete';
      flags_triggered_count: number;
      total_turns: number;
      completeness_pct: number;
    }
  | {
      type: 'outcomes';
      outcomes: Array<{ id: string; name: string; description: string; action: unknown }>;
    }
  | { type: 'emergency'; keyword: string | null; contact_text: string }
  | { type: 'error'; error: string }
  | { type: 'end' };

/**
 * Streaming turn: invia un messaggio e yielda eventi SSE in real-time.
 * Il chiamante consuma con `for await (const ev of streamTurn(...))`.
 * Fa reconnect automatico solo per errori di rete *prima* del primo byte;
 * mid-stream, abort è permanente (l'encounter rimane in_progress).
 */
export async function* streamTurn(
  sessionId: string,
  message: string,
  signal?: AbortSignal,
): AsyncGenerator<TurnStreamEvent, void, undefined> {
  const res = await fetch(`/api/session/${encodeURIComponent(sessionId)}/turn`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ message }),
    signal,
  });
  if (!res.ok) {
    const errBody: unknown = await res.json().catch(() => ({}));
    const parsed = ApiErrorSchema.safeParse(errBody);
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.error : `HTTP ${res.status}`,
      parsed.success ? (parsed.data.code ?? 'UNKNOWN') : 'UNKNOWN',
    );
  }
  if (!res.body) throw new ApiError(0, 'No response stream', 'NO_STREAM');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events: separati da \n\n
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;
        try {
          const ev = JSON.parse(dataLine.slice(6)) as TurnStreamEvent;
          yield ev;
          if (ev.type === 'end') return;
        } catch {
          // ignora event malformato
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ====================================================================
// Sprint 8: voice turn (multipart upload + SSE streaming response)
// ====================================================================

export type VoiceTurnEvent =
  | {
      type: 'transcription';
      text: string;
      language_detected: string;
      duration_seconds: number;
      confidence?: number;
    }
  | { type: 'text_delta'; text: string }
  | {
      type: 'final';
      status: 'in_progress' | 'completed' | 'aborted_emergency';
      next_action:
        | 'advance'
        | 'retry_same'
        | 'request_clarification'
        | 'emergency_exit'
        | 'complete';
      flags_triggered_count: number;
      total_turns: number;
      completeness_pct: number;
    }
  | {
      type: 'outcomes';
      outcomes: Array<{ id: string; name: string; description: string; action: unknown }>;
    }
  | { type: 'emergency'; keyword: string | null; contact_text: string }
  | { type: 'audio_chunk'; data: string }
  | { type: 'audio_end' }
  | { type: 'audio_error'; message: string }
  | { type: 'error'; error: string }
  | { type: 'end' };

/**
 * Upload audio (Blob) e ricevi stream SSE con transcript + LLM streaming + audio TTS.
 */
export async function* streamVoiceTurn(
  sessionId: string,
  audioBlob: Blob,
  signal?: AbortSignal,
): AsyncGenerator<VoiceTurnEvent, void, undefined> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'turn.webm');

  const res = await fetch(`/api/session/${encodeURIComponent(sessionId)}/voice/turn`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  if (!res.ok) {
    const errBody: unknown = await res.json().catch(() => ({}));
    const parsed = ApiErrorSchema.safeParse(errBody);
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.error : `HTTP ${res.status}`,
      parsed.success ? (parsed.data.code ?? 'UNKNOWN') : 'UNKNOWN',
    );
  }
  if (!res.body) throw new ApiError(0, 'No response stream', 'NO_STREAM');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;
        try {
          const ev = JSON.parse(dataLine.slice(6)) as VoiceTurnEvent;
          yield ev;
          if (ev.type === 'end') return;
        } catch {
          // ignora event malformato
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ====================================================================
// Resume: GET /api/session/:id
// ====================================================================

export interface ResumedMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  turn: number;
}

export interface ResumedSession {
  session_id: string;
  status: 'in_progress' | 'completed' | 'abandoned' | 'aborted_emergency';
  language_detected: string | null;
  language_script: string | null;
  pickup_code: string;
  total_turns: number;
  completeness_pct: number;
  emergency_triggered: boolean;
  messages: ResumedMessage[];
  outcomes_presented: Array<{ id: string; name: string; description: string; action: unknown }>;
  identity_collected: boolean;
}

const ResumedMessageSchema = z.object({
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  timestamp: z.string(),
  turn: z.number().int(),
});

const ResumedSessionResponseSchema = z.object({
  ok: z.literal(true),
  session_id: z.string().uuid(),
  status: z.enum(['in_progress', 'completed', 'abandoned', 'aborted_emergency']),
  language_detected: z.string().nullable(),
  language_script: z.string().nullable(),
  pickup_code: z.string(),
  total_turns: z.number().int(),
  completeness_pct: z.number().int(),
  emergency_triggered: z.boolean(),
  messages: z.array(ResumedMessageSchema),
  outcomes_presented: z.array(
    z.object({
      id: z.string().uuid(),
      outcomeId: z.string().uuid(),
      name: z.string(),
      description: z.string(),
      action: z.unknown(),
    }),
  ),
  // .default(false) per compat retroattiva: se il backend non è stato ancora
  // ridistribuito col nuovo campo, il client non si rompe.
  identity_collected: z.boolean().default(false),
});

export async function resumeSession(sessionId: string): Promise<ResumedSession> {
  const res = await request<unknown>(`/api/session/${encodeURIComponent(sessionId)}`);
  const parsed = ResumedSessionResponseSchema.parse(res);
  return {
    ...parsed,
    outcomes_presented: parsed.outcomes_presented.map((o) => ({
      id: o.id,
      name: o.name,
      description: o.description,
      action: o.action,
    })),
  };
}
